// Entrées PC : WASD/ZQSD (par position physique des touches, donc les deux dispositions), flèches,
// visée souris, clic maintenu pour tirer, E pour le tir automatique, 1–8 pour les statistiques.

export interface InputSample {
  mx: number;
  my: number;
  /** Visée en coordonnées écran relatives au tank (px), ou angle direct pour le tactile. */
  aimAngle: number;
  aimDist: number;
  fire: boolean;
}

export class KeyboardMouse {
  private keys = new Set<string>();
  private mouseX = 0;
  private mouseY = 0;
  private mouseDown = false;
  autofire = false;
  hasMouse = false;
  private disposers: (() => void)[] = [];

  constructor(
    private readonly el: HTMLElement,
    private readonly onStat: (i: number) => void,
    private readonly onAutofireToggle: (v: boolean) => void,
    private readonly onQuick: (i: number) => void = () => {},
  ) {
    const kd = (e: KeyboardEvent) => {
      if (isTyping(e)) return;
      this.keys.add(e.code);
      if (e.code === 'KeyE' && !e.repeat) {
        this.autofire = !this.autofire;
        this.onAutofireToggle(this.autofire);
      }
      const q = /^F([1-6])$/.exec(e.code);
      if (q && !e.repeat) {
        e.preventDefault();
        this.onQuick(Number(q[1]) - 1);
      }
      const m = /^Digit([1-8])$/.exec(e.code) ?? /^Numpad([1-8])$/.exec(e.code);
      if (m && !e.repeat) this.onStat(Number(m[1]) - 1);
      if (e.code.startsWith('Arrow') || e.code === 'Space') e.preventDefault();
    };
    const ku = (e: KeyboardEvent) => this.keys.delete(e.code);
    const blur = () => {
      this.keys.clear();
      this.mouseDown = false;
    };
    const mm = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      this.hasMouse = true;
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
    };
    const md = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Seuls les clics sur la zone de jeu tirent ; pas ceux sur les boutons du HUD.
      const target = e.target as HTMLElement | null;
      if (!target || !(target.tagName === 'CANVAS' || target.classList.contains('touch-layer'))) return;
      this.hasMouse = true;
      this.mouseDown = true;
    };
    const mu = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      this.mouseDown = false;
    };
    const ctx = (e: Event) => e.preventDefault();
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', blur);
    window.addEventListener('pointermove', mm);
    el.addEventListener('pointerdown', md);
    window.addEventListener('pointerup', mu);
    el.addEventListener('contextmenu', ctx);
    this.disposers.push(
      () => window.removeEventListener('keydown', kd),
      () => window.removeEventListener('keyup', ku),
      () => window.removeEventListener('blur', blur),
      () => window.removeEventListener('pointermove', mm),
      () => el.removeEventListener('pointerdown', md),
      () => window.removeEventListener('pointerup', mu),
      () => el.removeEventListener('contextmenu', ctx),
    );
  }

  /** `cx, cy` = position écran du tank ; `scale` = pixels par unité de monde. */
  sample(cx: number, cy: number, scale: number): InputSample {
    const k = this.keys;
    let mx = 0;
    let my = 0;
    if (k.has('KeyA') || k.has('ArrowLeft')) mx -= 1;
    if (k.has('KeyD') || k.has('ArrowRight')) mx += 1;
    if (k.has('KeyW') || k.has('ArrowUp')) my -= 1;
    if (k.has('KeyS') || k.has('ArrowDown')) my += 1;
    const l = Math.hypot(mx, my);
    if (l > 0) {
      mx /= l;
      my /= l;
    }
    const dx = this.mouseX - cx;
    const dy = this.mouseY - cy;
    return {
      mx,
      my,
      aimAngle: Math.atan2(dy, dx),
      aimDist: Math.hypot(dx, dy) / Math.max(scale, 1e-6),
      fire: this.mouseDown || this.autofire,
    };
  }

  dispose(): void {
    for (const d of this.disposers) d();
  }
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
}
